import type { ChangeEvent } from 'react';
import React, { useState } from 'react';

import Typography from '@mui/material/Typography';
import { Popover } from '@mui/material';
import Box from '@mui/material/Box';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import { useTranslation } from 'next-i18next';

import { useFormikContext } from 'formik';

import TextField from 'src/components/ui/TextField';

import { colours } from 'src/constants/colours';
import theme from 'src/utils/theme';
import { formatNumberApprox } from 'src/utils/getFormattedPrice';

import type { ExtendedAdsQueryVariables } from 'src/components/ad/SearchAdsPage';
import { styledComponent } from 'src/utils/styled';

const StyledButton = styledComponent(Box)<{ $opened: boolean }>`
    cursor: pointer;
    border-radius: 0.25rem;
    height: 2.5rem;
    background-color: ${colours.primary.O08};
    .MuiButton-label {
        color: ${theme.palette.text.secondary};
    }
    svg {
        fill: current-color;
    }
    ${({ $opened }) =>
        $opened &&
        `
        border: 0.0625rem solid ${colours.primary.O60};
        background-color: ${colours.blackOpacity.O08};
    `}
    :hover {
        background-color: ${colours.blackOpacity.O08};
    }
`;

const MinMaxInputs = styledComponent(Box)`
    .MuiFormControl-root {
        margin: 0.5rem;
    }
    .MuiInputBase-root {
        height: 2.5rem;
    }
    .MuiInputLabel-root {
        transform: translate(0.5rem, 0.5rem) scale(1)!important;
        &.MuiInputLabel-shrink,
        &.Mui-focused {
            display: none;
        }
    }
    .MuiFilledInput-input{
        padding-top: 0.5rem!important;
        padding-bottom: 0.5rem!important;
        padding-left: 0.45rem!important;
    }
`;

const StyledTextField = styledComponent(TextField)`
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
    }
    input[type='number'] {
        -moz-appearance: textfield;
    }
`;

interface Props {
    onChangeMin?: (value: string) => void;
    onChangeMax?: (value: string) => void;
    queryVariables: ExtendedAdsQueryVariables;
}

const BudgetButton: React.FC<Props> = ({ onChangeMin, onChangeMax, queryVariables }) => {
    const { t } = useTranslation(['ad']);
    const formikContext = useFormikContext<ExtendedAdsQueryVariables>();

    const [budgetAnchorEl, setBudgetAnchorEl] = useState<HTMLDivElement | null>(null);
    const budgetOpen = Boolean(budgetAnchorEl);
    const budgetPopoverId = budgetOpen ? 'budget-popover' : undefined;
    const handleBudgetPopoverOpen = (event: React.MouseEvent<HTMLDivElement>) => {
        setBudgetAnchorEl(event.currentTarget);
    };

    const handleBudgetPopoverClose = () => {
        setBudgetAnchorEl(null);
    };
    const handleBudgetMax = (ev: ChangeEvent<{ value: string }>) => {
        onChangeMax?.(ev.target.value);
    };
    const handleBudgetMin = (ev: ChangeEvent<{ value: string }>) => {
        onChangeMin?.(ev.target.value);
    };

    const getBudgetButtonLabel = () => {
        let label = t('ad:budget');
        const minPrice = Number(formikContext.values.minPrice);
        const maxPrice = Number(formikContext.values.maxPrice);
        if (formikContext.values.maxPrice || formikContext.values.minPrice) {
            label = `${
                formikContext.values.minPrice ? formatNumberApprox(minPrice) : t('ad:min')
            } ${formikContext.values.minPrice ? '€' : ''} - ${
                formikContext.values.maxPrice ? formatNumberApprox(maxPrice) : t('ad:max')
            } ${formikContext.values.maxPrice ? '€' : ''}`;
        }
        return label;
    };

    return (
        <>
            <StyledButton
                $opened={budgetOpen}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                pl="0.75rem"
                data-target-id="budget-button"
                aria-describedby={budgetPopoverId}
                onClick={handleBudgetPopoverOpen}
            >
                <Typography variant="body2" color={colours.primary.main} noWrap>
                    {getBudgetButtonLabel()}
                </Typography>
                <Box display="flex" margin="auto 0.5rem auto 0" style={{ opacity: 0.8 }}>
                    {budgetOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </Box>
            </StyledButton>
            <Popover
                id={budgetPopoverId}
                open={budgetOpen}
                anchorEl={budgetAnchorEl}
                onClose={handleBudgetPopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <MinMaxInputs display="flex" flexDirection="row" width="14rem">
                    <StyledTextField
                        type="number"
                        maxLength={9}
                        name="minPrice"
                        label={queryVariables.minPrice ? '' : t('ad:min')}
                        onChange={handleBudgetMin}
                        minAndMaxBudgetButton
                    />
                    <StyledTextField
                        type="number"
                        maxLength={9}
                        name="maxPrice"
                        data-target-id="maxPrice"
                        label={queryVariables.maxPrice ? '' : t('ad:max')}
                        onChange={handleBudgetMax}
                        minAndMaxBudgetButton
                    />
                </MinMaxInputs>
            </Popover>
        </>
    );
};

export default BudgetButton;
